// packages/engine/src/process-contexts.ts
//
// Walker + swimlane context builders over a ResolvedModel (plan
// 2026-07-07-process-scaffold-bpmn-diff P4.0). These port the scope walks
// that previously lived only in app-shell's useProcessOrchestrator (wire-model
// based) so per-side diff scaffolding — which has ResolvedModels, not wire
// models — can build identical contexts. The app-shell hook's semantics are
// the contract:
//   - subprocess scope: top-level global < module-scoped by owner-prefix
//     (nearest last) < process-nested (`ownerProcess` discriminator);
//   - `do <Process>` fallback: same walk over named processes;
//   - swimlane resolution: hoist a written module path to its full qualified
//     path via the unique-suffix index; callers resolve to themselves when
//     they name a module, else through a bind alias to the bound callee's
//     module; a single unknown segment is an actor.
//
// One deliberate delta from the hook: a CALLER naming a nested module by its
// bare unique name resolves to the FULL path here (the hook returned the raw
// segments, splitting one module across two lanes). Diff matching keys on RAW
// paths, so this only improves owner display — but keep it in mind if these
// helpers ever replace the hook's wire-model context.

import type { QualifiedName } from "./types.js";
import type { ResolvedModel, ResolvedModule } from "./resolver.js";
import type { ProcessModel, SubprocessModel } from "./process-model.js";
import type { WalkerContext } from "./process-walker-types.js";
import type { SwimlaneContext } from "./process-swimlane-types.js";
import { forEachNode } from "./process-ir-walk.js";
import { buildSpliceScope } from "./splice-scope.js";

/** The walker's subprocess/process scope for one process, over the IR.
 *  v0.11: scope walks live in the shared space-aware builder
 *  (splice-scope.ts) — the SAME one graph edge derivation consumes — so
 *  walker and edges can never drift. */
export function walkerContextFor(model: ResolvedModel, proc: ProcessModel): WalkerContext {
  const activeOwner = proc.ownerPath ?? [];
  const qualified =
    activeOwner.length > 0 ? `${activeOwner.join(".")}.${proc.name}` : proc.name;
  // v0.11 (D2, rev-1 F6): space-qualified when the process is spaced — must
  // match the builder's `ownerProcess` discriminator byte-for-byte.
  const activeIdentity =
    proc.id ??
    (proc.space && proc.space.length > 0 ? `${proc.space.join(".")}::${qualified}` : qualified);

  // Process-nested subprocesses (the `ownerProcess` layer) win last and stay
  // outside the shared builder; other processes' private subs are out of scope.
  const nestedSubs: SubprocessModel[] = [];
  const shared: SubprocessModel[] = [];
  for (const sm of model.subprocessModels ?? []) {
    if (sm.ownerProcess) {
      if (sm.ownerProcess === activeIdentity) nestedSubs.push(sm);
      continue;
    }
    shared.push(sm);
  }
  const subprocessByName = buildSpliceScope(shared, proc);
  for (const sm of nestedSubs) subprocessByName.set(sm.name, sm);

  const named = (model.processModels ?? []).filter((pm) => !!pm.name && !pm.anonymous) as (ProcessModel & {
    name: string;
  })[];
  const processByName = buildSpliceScope(named, proc);

  return { subprocessByName, processByName };
}

/** Swimlane/scaffold semantic resolution over the resolved module tree. */
export function swimlaneContextFor(model: ResolvedModel): SwimlaneContext {
  // Unique-suffix hoist index (v0.8.2 §4.11): a written short ref resolves to
  // its full path when the suffix is unambiguous.
  const byPath = new Set<string>();
  const sufCount = new Map<string, number>();
  const sufOne = new Map<string, string>();
  const names = new Set<string>();
  (function index(mods: readonly ResolvedModule[], parent: readonly string[]) {
    for (const m of mods) {
      const path = [...parent, m.name];
      byPath.add(path.join("."));
      names.add(m.name);
      for (let k = 1; k < path.length; k++) {
        const suf = path.slice(path.length - k).join(".");
        sufCount.set(suf, (sufCount.get(suf) ?? 0) + 1);
        sufOne.set(suf, path.join("."));
      }
      index(m.modules, path);
    }
  })(model.modules, []);

  const hoist = (segs: readonly string[]): QualifiedName => {
    const key = segs.join(".");
    if (byPath.has(key)) return segs.slice() as QualifiedName;
    const full = sufCount.get(key) === 1 ? sufOne.get(key) : undefined;
    return (full ? full.split(".") : segs.slice()) as QualifiedName;
  };
  const isKnownModule = (segs: readonly string[]): boolean => {
    const key = segs.join(".");
    return byPath.has(key) || sufCount.get(key) === 1 || (segs.length === 1 && names.has(segs[0]!));
  };

  // Bind-alias fallback: `order = A > Orders.create ; order > X` — the alias
  // resolves to the bound callee's module.
  const bindModule = (name: string): QualifiedName | null => {
    for (const pm of model.processModels ?? []) {
      let found: QualifiedName | null = null;
      forEachNode(pm.body, (node) => {
        if (found || node.kind !== "call" || !node.bind || node.bind.name !== name) return;
        const last = node.hops[node.hops.length - 1];
        if (!last) return;
        found = (last.callee.anonymous
          ? last.callee.modulePath.slice()
          : last.callee.interfacePath.slice(0, -1)) as QualifiedName;
      });
      if (found) return found;
    }
    return null;
  };

  return {
    resolveCalleeModule: (to) => hoist(to.slice(0, -1)),
    resolveCallerModule: (from) => {
      if (from.length > 0 && isKnownModule(from)) return hoist(from);
      if (from.length === 1) return bindModule(from[0]!);
      return null;
    },
    isActor: (from) => from.length === 1 && !names.has(from[0]!) && !byPath.has(from[0]!),
  };
}
